import { LotEventTypes, LotInspectionResults, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotEventToFirestore, lotFromFirestore, lotInspectionToFirestore } from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Record a QC inspection for one lot, in one transaction: the
 * lotInspections record, the lot's status, and an `inspected` event
 * referencing the record (plus a `status_changed` event when the status
 * moves). The status decision comes from a fresh in-transaction read —
 * never the page's stale copy — so a Passed submitted after someone
 * else quarantined the bag can't lift the quarantine. Status rules:
 * Failed quarantines (a no-op if already Quarantined); Passed seals the
 * lot only from Awaiting inspection — a re-inspected lot that's already
 * in circulation keeps its current status, the record just documents it.
 */
export default async function recordLotInspection({
  lot, result, note, dataLinks = [], user,
}) {
  const db = getFirestore();
  const inspectionRef = doc(collection(db, 'lotInspections'));
  const lotRef = doc(db, 'lots', lot.id);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(lotRef);
    if (!snap.exists()) throw new Error('Lot not found');
    const freshLot = lotFromFirestore(snap);

    let newStatus = null;
    if (result === LotInspectionResults.FAILED
      && freshLot.status !== LotStatus.QUARANTINED) {
      newStatus = LotStatus.QUARANTINED;
    } else if (result === LotInspectionResults.PASSED
      && freshLot.status === LotStatus.AWAITING_INSPECTION) {
      newStatus = LotStatus.SEALED;
    }

    transaction.set(inspectionRef, lotInspectionToFirestore({
      lotIds: [lot.id],
      result,
      note,
      dataLinks,
      _createdBy: user,
    }, serverTimestamp));

    transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.INSPECTED,
      reason: [result, note].filter(Boolean).join(' — '),
      relatedIds: { inspectionId: inspectionRef.id },
      _createdBy: user,
    }, serverTimestamp));

    if (newStatus) {
      transaction.update(lotRef, { status: newStatus, statusChangedAt: serverTimestamp() });
      transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
        type: LotEventTypes.STATUS_CHANGED,
        reason: `${newStatus} — inspection ${result}`,
        relatedIds: { inspectionId: inspectionRef.id },
        _createdBy: user,
      }, serverTimestamp));
    }
  });

  return { id: inspectionRef.id };
}
